import { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import {
  AlertTriangle,
  Check,
  ChevronRight,
  ExternalLink,
  FileText,
  Loader2,
  Receipt,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  AccountantInboxAckItem,
  AccountantInboxBoletoItem,
  AccountantInboxObligationItem,
} from "@fiscalo/shared-types";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { bandejaKeys } from "@/lib/queryKeys";
import { useTenantSwitcher } from "@/contexts/TenantSwitcherContext";
import {
  accountantAcceptRecibida,
  accountantMarkObligationPaid,
  accountantRejectRecibida,
} from "@/services/dashboardService";
import {
  ackUrgencyLabel,
  formatMoneyEs,
  obligationUrgencyLabel,
  tenantInitials,
} from "@/components/features/accountant/BandejaRowMeta";

export type BandejaRowKind = "ack" | "boleto" | "obligation";

interface BaseProps {
  /** When true, render compact (cartera-home preview). Default is the
   *  expanded full-page layout. */
  compact?: boolean;
  /** When true, show the V2 inline action affordances. Off in V1. */
  withInlineActions?: boolean;
  /** When provided, render a checkbox at the start of the row. Click
   *  on the row body toggles selection instead of opening the deep
   *  link — V3 bulk-mode UX. Inline action buttons still work. */
  selection?: {
    selected: boolean;
    onToggle: () => void;
  };
  /** True while a bulk action is in flight for this specific row.
   *  Triggers the same fade-out animation single-item inline accept
   *  uses, so the bulk operation feels of-a-piece. */
  bulkPending?: boolean;
}

export interface BandejaAckRowProps extends BaseProps {
  kind: "ack";
  item: AccountantInboxAckItem;
  /** Accept every currently-listed pending comprobante from this row's emisor
   *  in one action. Provided by the acks page; the button only shows when
   *  `emisorPendingCount` > 1 (otherwise plain "Aceptar" already covers it). */
  onAcceptAllFromEmisor?: () => void;
  /** How many listed pending comprobantes share this row's emisor. */
  emisorPendingCount?: number;
}

export interface BandejaBoletoRowProps extends BaseProps {
  kind: "boleto";
  item: AccountantInboxBoletoItem;
}

export interface BandejaObligationRowProps extends BaseProps {
  kind: "obligation";
  item: AccountantInboxObligationItem;
}

export type BandejaRowProps =
  | BandejaAckRowProps
  | BandejaBoletoRowProps
  | BandejaObligationRowProps;

/**
 * Single bandeja row, motion-aware, brand-aligned. Used by both the
 * cartera-home preview (compact=true) and the full-list pages. Inline
 * actions (V2) flip on via `withInlineActions`; default V1 behaviour
 * is "click row → switch tenant + deep-link to detail" so the
 * accountant resolves the item in the SMB-facing UI.
 */
export function BandejaRow(props: BandejaRowProps): JSX.Element {
  const navigate = useNavigate();
  const { switchTenant, isSwitchingTenant } = useTenantSwitcher();
  const reduceMotion = useReducedMotion();

  const [pendingFade, setPendingFade] = useState(false);
  const effectivePendingFade = pendingFade || Boolean(props.bulkPending);
  const inSelectionMode = props.selection != null;

  // Each kind has its own URL target for "row click" → switch + deep-link.
  const tenantId = props.item.tenant.id;
  const deepLink =
    props.kind === "ack"
      ? `/invoices/recibidas/${props.item.recibidaId}`
      : `/fiscal-status`;
  const tenantDisplayName =
    props.item.tenant.legalName ??
    props.item.tenant.name ??
    props.item.tenant.rut ??
    "—";

  const onRowClick = async (): Promise<void> => {
    // In selection mode, the whole row body is a giant checkbox — much
    // easier to multi-pick than aiming at the tiny square. Inline
    // action buttons stop propagation so they still work.
    if (inSelectionMode) {
      props.selection!.onToggle();
      return;
    }
    try {
      await switchTenant(tenantId);
      navigate(deepLink);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "No pudimos abrir el cliente. Probá de nuevo.",
      );
    }
  };

  const animProps = reduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 4 },
        animate: {
          opacity: effectivePendingFade ? 0 : 1,
          y: effectivePendingFade ? -6 : 0,
        },
        exit: { opacity: 0, x: 24 },
        transition: { duration: 0.2, ease: [0.22, 1, 0.36, 1] as const },
      };

  return (
    <motion.div layout {...animProps} className="contents">
      <button
        type="button"
        onClick={() => void onRowClick()}
        disabled={
          (isSwitchingTenant && !inSelectionMode) || effectivePendingFade
        }
        className={cn(
          "group flex w-full items-center gap-3 rounded-lg border border-transparent px-3 py-2.5 text-left transition-colors",
          "hover:border-[var(--fx-ink-100)] hover:bg-[var(--fx-s-raised-hover)]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--fx-accent-300)]",
          "disabled:cursor-default disabled:opacity-60",
          props.selection?.selected &&
            "border-[var(--fx-accent-300)] bg-[var(--fx-accent-100)]/40",
          props.compact && "py-2",
        )}
      >
        {props.selection ? (
          <Checkbox
            checked={props.selection.selected}
            onCheckedChange={() => props.selection!.onToggle()}
            onClick={(e) => e.stopPropagation()}
            aria-label="Seleccionar comprobante"
            className="shrink-0"
          />
        ) : null}
        <TenantAvatar
          name={tenantDisplayName}
          profileImage={props.item.tenant.profileImage}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium text-foreground">
              {tenantDisplayName}
            </span>
            <UrgencyChip kind={props.kind} item={props.item} />
          </div>
          <div className="mt-0.5 truncate text-xs text-muted-foreground">
            <RowDescription kind={props.kind} item={props.item} />
          </div>
        </div>
        {!props.withInlineActions ? (
          <ChevronRight
            className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
            aria-hidden
          />
        ) : null}
      </button>
      {props.withInlineActions ? (
        <InlineActions
          kind={props.kind}
          item={props.item}
          onResolved={() => setPendingFade(true)}
          onAcceptAllFromEmisor={
            props.kind === "ack" ? props.onAcceptAllFromEmisor : undefined
          }
          emisorPendingCount={
            props.kind === "ack" ? props.emisorPendingCount : undefined
          }
        />
      ) : null}
    </motion.div>
  );
}

function TenantAvatar({
  name,
  profileImage,
}: {
  name: string;
  profileImage: string | null;
}): JSX.Element {
  return (
    <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[var(--fx-paper-3)] text-sm font-semibold text-[var(--fx-ink-700)] dark:bg-[var(--dk-surface2)]">
      {profileImage ? (
        <img
          src={profileImage}
          alt={name}
          className="h-full w-full object-cover"
        />
      ) : (
        tenantInitials(name)
      )}
    </div>
  );
}

function UrgencyChip({
  kind,
  item,
}: {
  kind: BandejaRowKind;
  item:
    | AccountantInboxAckItem
    | AccountantInboxBoletoItem
    | AccountantInboxObligationItem;
}): JSX.Element {
  const overdue = "overdue" in item ? item.overdue : false;
  const label =
    kind === "ack"
      ? ackUrgencyLabel(item as AccountantInboxAckItem)
      : obligationUrgencyLabel(
          item as AccountantInboxBoletoItem | AccountantInboxObligationItem,
        );

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide",
        overdue
          ? "bg-[var(--fx-warn)]/10 text-[var(--fx-warn-text)]"
          : "bg-[var(--fx-ink-100)] text-[var(--fx-ink-700)]",
      )}
    >
      {overdue ? <AlertTriangle className="h-3 w-3" aria-hidden /> : null}
      {label}
    </span>
  );
}

function RowDescription({
  kind,
  item,
}: {
  kind: BandejaRowKind;
  item:
    | AccountantInboxAckItem
    | AccountantInboxBoletoItem
    | AccountantInboxObligationItem;
}): JSX.Element {
  if (kind === "ack") {
    const ack = item as AccountantInboxAckItem;
    const issuer = ack.rucEmisorLegalName?.trim() || `RUT ${ack.rucEmisor}`;
    return (
      <span>
        <Receipt className="mr-1 inline-block h-3 w-3 align-[-2px]" aria-hidden />
        {ack.tipoCFE} {ack.serie}-{ack.nro} · {issuer} · {formatMoneyEs(ack.moneda, ack.montoTotal)}
      </span>
    );
  }
  if (kind === "boleto") {
    const o = item as AccountantInboxBoletoItem;
    return (
      <span>
        <FileText className="mr-1 inline-block h-3 w-3 align-[-2px]" aria-hidden />
        {o.taxType} {o.period} · vence {o.dueDate}
      </span>
    );
  }
  const o = item as AccountantInboxObligationItem;
  return (
    <span>
      <FileText className="mr-1 inline-block h-3 w-3 align-[-2px]" aria-hidden />
      {o.taxType} ({o.authority}) {o.period} · vence {o.dueDate}
    </span>
  );
}

/* ---------- V2 inline actions ---------- */

function InlineActions({
  kind,
  item,
  onResolved,
  onAcceptAllFromEmisor,
  emisorPendingCount,
}: {
  kind: BandejaRowKind;
  item:
    | AccountantInboxAckItem
    | AccountantInboxBoletoItem
    | AccountantInboxObligationItem;
  onResolved: () => void;
  onAcceptAllFromEmisor?: () => void;
  emisorPendingCount?: number;
}): JSX.Element {
  if (kind === "ack") {
    return (
      <AckInlineActions
        item={item as AccountantInboxAckItem}
        onResolved={onResolved}
        onAcceptAllFromEmisor={onAcceptAllFromEmisor}
        emisorPendingCount={emisorPendingCount}
      />
    );
  }
  if (kind === "boleto" || kind === "obligation") {
    return (
      <ObligationInlineActions
        kind={kind}
        item={
          item as AccountantInboxBoletoItem | AccountantInboxObligationItem
        }
        onResolved={onResolved}
      />
    );
  }
  return <></>;
}

function AckInlineActions({
  item,
  onResolved,
  onAcceptAllFromEmisor,
  emisorPendingCount,
}: {
  item: AccountantInboxAckItem;
  onResolved: () => void;
  onAcceptAllFromEmisor?: () => void;
  emisorPendingCount?: number;
}): JSX.Element {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { switchTenant, isSwitchingTenant } = useTenantSwitcher();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [motivoCode, setMotivoCode] = useState("");
  const [motivoDesc, setMotivoDesc] = useState("");
  const [motivoDetail, setMotivoDetail] = useState("");

  const acceptMutation = useMutation({
    mutationFn: () =>
      accountantAcceptRecibida(item.tenant.id, item.recibidaId),
    onSuccess: () => {
      onResolved();
      toast.success("Comprobante aceptado");
      void qc.invalidateQueries({ queryKey: bandejaKeys.all });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "No pudimos aceptar");
    },
  });

  const rejectMutation = useMutation({
    mutationFn: () =>
      accountantRejectRecibida(item.tenant.id, item.recibidaId, [
        {
          code: motivoCode.trim() || "OTRO",
          description: motivoDesc.trim() || "Rechazo desde bandeja",
          detail: motivoDetail.trim() || undefined,
        },
      ]),
    onSuccess: () => {
      setRejectOpen(false);
      onResolved();
      toast.success("Comprobante rechazado");
      void qc.invalidateQueries({ queryKey: bandejaKeys.all });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "No pudimos rechazar");
    },
  });

  const openInvoice = async (): Promise<void> => {
    // Same deep-link the non-selection row click uses: switch into the tenant,
    // then open the recibida in the SMB-facing detail. Lets the accountant
    // inspect the actual comprobante before accepting/rejecting.
    try {
      await switchTenant(item.tenant.id);
      navigate(`/invoices/recibidas/${item.recibidaId}`);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "No pudimos abrir el comprobante.",
      );
    }
  };

  return (
    <div className="ml-12 flex items-center gap-2 pl-1 pb-2">
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => acceptMutation.mutate()}
        disabled={acceptMutation.isPending || rejectMutation.isPending}
        className="h-8 gap-1.5 border-[var(--fx-accent-300)] text-[var(--fx-accent-500)] hover:bg-[var(--fx-accent-100)]"
      >
        {acceptMutation.isPending ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Check className="h-3.5 w-3.5" />
        )}
        Aceptar
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        onClick={() => void openInvoice()}
        disabled={isSwitchingTenant}
        className="h-8 gap-1.5 text-muted-foreground hover:text-foreground"
      >
        <ExternalLink className="h-3.5 w-3.5" />
        Ver
      </Button>
      {onAcceptAllFromEmisor && emisorPendingCount && emisorPendingCount > 1 ? (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onAcceptAllFromEmisor}
          disabled={acceptMutation.isPending || rejectMutation.isPending}
          className="h-8 gap-1.5 text-[var(--fx-accent-500)] hover:bg-[var(--fx-accent-100)]"
        >
          <Check className="h-3.5 w-3.5" />
          Todas de este emisor ({emisorPendingCount})
        </Button>
      ) : null}
      <Popover open={rejectOpen} onOpenChange={setRejectOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={acceptMutation.isPending || rejectMutation.isPending}
            className="h-8 gap-1.5 text-muted-foreground hover:text-[var(--fx-warn-text)]"
          >
            <X className="h-3.5 w-3.5" />
            Rechazar
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="motivo-code">Código DGI</Label>
            <Input
              id="motivo-code"
              value={motivoCode}
              onChange={(e) => setMotivoCode(e.target.value)}
              placeholder="1, 2, 3…"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="motivo-desc">Descripción</Label>
            <Input
              id="motivo-desc"
              value={motivoDesc}
              onChange={(e) => setMotivoDesc(e.target.value)}
              placeholder="Ej: No conformidad con el monto"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="motivo-detail">Detalle (opcional)</Label>
            <Textarea
              id="motivo-detail"
              rows={2}
              value={motivoDetail}
              onChange={(e) => setMotivoDetail(e.target.value)}
            />
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setRejectOpen(false)}
              disabled={rejectMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => rejectMutation.mutate()}
              disabled={rejectMutation.isPending}
              className="bg-[var(--fx-warn)] hover:bg-[var(--fx-warn)]/90"
            >
              {rejectMutation.isPending ? (
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
              ) : null}
              Rechazar
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function ObligationInlineActions({
  kind,
  item,
  onResolved,
}: {
  kind: "boleto" | "obligation";
  item: AccountantInboxBoletoItem | AccountantInboxObligationItem;
  onResolved: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");

  const markPaidMutation = useMutation({
    mutationFn: () => {
      const value = parseFloat(amount.replace(/\./g, "").replace(",", "."));
      if (!Number.isFinite(value) || value < 0) {
        throw new Error("Monto inválido");
      }
      return accountantMarkObligationPaid(
        item.tenant.id,
        item.obligationId,
        value,
      );
    },
    onSuccess: () => {
      setOpen(false);
      onResolved();
      toast.success(
        kind === "boleto" ? "Boleto marcado como pagado" : "Obligación marcada como pagada",
      );
      void qc.invalidateQueries({ queryKey: bandejaKeys.all });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "No pudimos guardar");
    },
  });

  return (
    <div className="ml-12 flex items-center gap-2 pl-1 pb-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 gap-1.5 border-[var(--fx-accent-300)] text-[var(--fx-accent-500)] hover:bg-[var(--fx-accent-100)]"
          >
            <Check className="h-3.5 w-3.5" />
            Marcar pagada
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="paid-amount">Monto pagado (UYU)</Label>
            <Input
              id="paid-amount"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0,00"
            />
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setOpen(false)}
              disabled={markPaidMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => markPaidMutation.mutate()}
              disabled={markPaidMutation.isPending || !amount.trim()}
            >
              {markPaidMutation.isPending ? (
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
              ) : null}
              Guardar
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

/**
 * Wraps a sequence of rows in <AnimatePresence> so removal animations
 * fire when rows resolve in V2. Use in any consumer of <BandejaRow>.
 */
export function BandejaRowList({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  return <AnimatePresence initial={false}>{children}</AnimatePresence>;
}
